import type { ContentBlock } from "@/features/content/content-types";
import type { NewsPostData, NewsMeta } from "@/features/news/types";
import { NEWS_META } from "@/features/news/generated/meta";
import { locales, defaultLocale, type Locale } from "@/lib/i18n/config";
import { registerLocaleResolver } from "@/lib/i18n/locale-availability";

const englishMeta = NEWS_META[defaultLocale] ?? {};

/** Every English post's meta, localized where a translated file exists — see
 * guides/registry.ts#metaForLocale for the same fallback shape. */
function metaForLocale(locale: Locale): Record<string, NewsMeta> {
  const localized = NEWS_META[locale] ?? {};
  const merged: Record<string, NewsMeta> = {};
  for (const [id, fallback] of Object.entries(englishMeta)) {
    merged[id] = localized[id] ?? fallback;
  }
  return merged;
}

/** Locales with a real translated source file for this post — see
 * guides/registry.ts#localesForGuide. Keyed on meta.id, not the module filename — that is the key
 * generate-static-meta.ts uses for news, and the two must agree. */
export function localesForNews(id: string): Locale[] {
  return locales.filter((locale) => Object.values(NEWS_META[locale] ?? {}).some((meta) => meta.id === id));
}

/** Body text (blocks[]) is loaded lazily — see loadNewsBlocks below and
 * guides/registry.ts#toGuide for why this mirrors that file exactly. */
function toPost(meta: NewsMeta, locale: Locale): NewsPostData {
  return { meta, blocks: BLOCKS_CACHE.get(cacheKey(locale, meta.id)) ?? [] };
}

// See guides/registry.ts — same rule, same reason for registering instead of importing.
registerLocaleResolver("news", localesForNews);

export function getAllNews(locale: Locale = defaultLocale): NewsPostData[] {
  return Object.values(metaForLocale(locale))
    .map((meta) => toPost(meta, locale))
    .sort((a, b) => new Date(b.meta.date).getTime() - new Date(a.meta.date).getTime());
}

export function getNewsById(locale: Locale, id: string): NewsPostData | undefined {
  const meta = metaForLocale(locale)[id];
  return meta ? toPost(meta, locale) : undefined;
}

// ---- Lazy body loading ----

/** See guides/registry.ts#contentLoaders — same reasoning, same shape. */
const contentLoaders = import.meta.glob<{ post: NewsPostData }>("./content/*/*.ts", { eager: false });

const BLOCKS_CACHE = new Map<string, ContentBlock[]>();
const cacheKey = (locale: Locale, id: string) => `${locale}:${id}`;

/** See guides/registry.ts#loadGuideBlocks — same reasoning, same shape. News' filename, meta.id
 * and the `/news/:id` route param are all the same string by convention (checked in
 * generate-content-meta.ts), so no separate slug-to-id lookup is needed here. */
export async function loadNewsBlocks(locale: Locale, id: string): Promise<void> {
  const key = cacheKey(locale, id);
  if (BLOCKS_CACHE.has(key)) return;
  const loader =
    contentLoaders[`./content/${locale}/${id}.ts`] ?? contentLoaders[`./content/${defaultLocale}/${id}.ts`];
  if (!loader) return;
  const mod = await loader();
  BLOCKS_CACHE.set(key, mod.post.blocks);
}

/** What main.tsx calls for a `/news/:id` route before hydrating. */
export const preloadNewsById = loadNewsBlocks;

/** SSR-only: preloads every post's body, every locale — see entry-server.tsx's
 * preloadTranslations / guides/registry.ts#loadAllGuideBlocks. */
export async function loadAllNewsBlocks(): Promise<void> {
  await Promise.all(
    locales.flatMap((locale) => Object.keys(metaForLocale(locale)).map((id) => loadNewsBlocks(locale, id))),
  );
}
